import React from "react";
import api from "@/lib/api";
import { openFile } from "@/lib/fileViewer";
import { EUR2, fmtDate } from "@/lib/format";
import { ArrowDownLeft, ArrowUpRight, Gift, Paperclip } from "lucide-react";
import { toast } from "sonner";

export const METHOD_LABEL = { bonifico: "Bonifico", paypal: "PayPal", stripe: "Stripe", contanti: "Contanti", altro: "Altro" };
export const METHOD_ACCOUNT_KIND = { bonifico: "bank", paypal: "paypal", stripe: "stripe", contanti: "cash" };
export const KIND_LABEL = { ricarica: "Ricarica", accredito: "Credito assegnato", addebito: "Addebito", pacchetto: "Pacchetto prepagato" };

const STATUS = {
  pending:  { label: "In attesa",  cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Approvata",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rifiutata",  cls: "bg-red-50 text-red-700 border-red-200" },
  annullata:{ label: "Annullata",  cls: "bg-stone-100 text-stone-600 border-stone-200" },
};

export function TopupBadge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`} data-testid={`topup-status-${status}`}>{s.label}</span>;
}

export function Money({ value, className = "" }) {
  const neg = value < 0;
  return <span className={`tabular-nums ${neg ? "text-red-700" : ""} ${className}`}>{EUR2.format(value || 0)}</span>;
}

export async function openReceipt(topupId) {
  try {
    const res = await api.get(`/wallet/topups/${topupId}/receipt`, { responseType: "blob" });
    openFile(URL.createObjectURL(res.data), "Ricevuta.pdf");
  } catch {
    toast.error("Impossibile aprire la ricevuta");
  }
}

export function ReceiptLink({ topup }) {
  if (!topup?.receipt) return null;
  return (
    <button type="button" onClick={() => openReceipt(topup.id)} className="inline-flex items-center gap-1 text-[11px] text-[#0F766E] hover:underline" data-testid={`topup-receipt-${topup.id}`}>
      <Paperclip size={11}/> {topup.receipt.filename || "Ricevuta"}
    </button>
  );
}

function TxIcon({ kind }) {
  if (kind === "addebito" || kind === "pacchetto") return <ArrowUpRight size={14} className="text-red-600"/>;
  if (kind === "accredito") return <Gift size={14} className="text-indigo-600"/>;
  return <ArrowDownLeft size={14} className="text-emerald-600"/>;
}

export function TxList({ transactions = [], onDelete }) {
  if (!transactions.length) return <div className="p-6 text-center text-sm text-stone-400">Nessun movimento.</div>;
  return (
    <ul className="divide-y divide-stone-100" data-testid="wallet-tx-list">
      {transactions.map((t) => (
        <li key={t.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`wallet-tx-${t.id}`}>
          <div className="w-8 h-8 rounded-full bg-stone-50 border border-stone-100 grid place-items-center shrink-0"><TxIcon kind={t.kind}/></div>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{KIND_LABEL[t.kind] || t.kind}{t.method && <span className="text-stone-500 font-normal"> · {METHOD_LABEL[t.method] || t.method}</span>}</div>
            <div className="text-[11px] text-stone-500 truncate">
              {fmtDate(t.date)}{t.description && ` · ${t.description}`}{t.reference && ` · Rif. ${t.reference}`}
              {t.admin_movement_id && " · registrata in cassa"}
            </div>
          </div>
          <Money value={t.amount} className="font-semibold"/>
          {onDelete && (
            <button type="button" onClick={() => onDelete(t)} className="text-[11px] text-stone-400 hover:text-red-600" data-testid={`wallet-tx-del-${t.id}`}>Elimina</button>
          )}
        </li>
      ))}
    </ul>
  );
}
